import { Button, buttonVariants } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { getTorrentMetadataFromEvent } from '@/lib/event-metadata'
import { cn } from '@/lib/utils'
import { Event } from '@nostr/tools/wasm'
import { HugeiconsIcon } from '@hugeicons/react'
import { Copy01Icon, DownloadIcon } from '@hugeicons/core-free-icons'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

export function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0 || isNaN(bytes)) return ''
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let n = bytes
  let i = 0
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024
    i++
  }
  return `${n >= 100 || i === 0 ? Math.round(n) : n.toFixed(1)} ${units[i]}`
}

const MAX_SHOWN_FILES = 20
const MAX_SHOWN_TRACKERS = 10

export default function Torrent({ event, className }: { event: Event; className?: string }) {
  const { t } = useTranslation()
  const metadata = useMemo(() => getTorrentMetadataFromEvent(event), [event])

  const magnet = useMemo(() => {
    if (!metadata.infoHash) return undefined
    let uri = `magnet:?xt=urn:btih:${metadata.infoHash}`
    for (const hash of metadata.infoHash) uri += `&xt=urn:btih:${hash}`
    uri += `&dn=${encodeURIComponent(metadata.title)}`
    for (const tracker of metadata.trackers) uri += `&tr=${encodeURIComponent(tracker)}`
    return uri
  }, [metadata])

  const shownFiles = metadata.files.slice(0, MAX_SHOWN_FILES)

  return (
    <div className={cn('space-y-2', className)}>
      <div className="text-lg font-semibold break-words">{metadata.title}</div>
      {(metadata.tags.length > 0 || event.content) && (
        <div className="flex items-center gap-2">
          {metadata.tags.map((tag) => (
            <Badge key={tag} variant="secondary">
              {tag}
            </Badge>
          ))}
        </div>
      )}
      {event.content && (
        <div className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-6 break-words">
          {event.content}
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {magnet && (
          <>
            <a
              className={buttonVariants({ variant: 'outline', size: 'sm' })}
              href={magnet}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <HugeiconsIcon icon={DownloadIcon} />
              {t('Open in torrent app')}
            </a>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                navigator.clipboard.writeText(magnet)
                toast.success(t('Magnet link copied to clipboard'))
              }}
            >
              <HugeiconsIcon icon={Copy01Icon} />
              {t('Copy magnet')}
            </Button>
          </>
        )}
      </div>
      {metadata.files.length > 0 && (
        <div className="space-y-1">
          <div className="text-sm font-medium">
            {t('{{count}} files · {{size}}', {
              count: metadata.files.length,
              size: formatBytes(metadata.totalSize),
              defaultValue: `${metadata.files.length} files`
            })}
          </div>
          {shownFiles.map((file, i) => (
            <div key={i} className="flex items-center justify-between gap-2 text-sm">
              <span className="font-mono text-xs text-muted-foreground truncate">{file.name}</span>
              {file.size !== undefined && (
                <span className="text-muted-foreground shrink-0">{formatBytes(file.size)}</span>
              )}
            </div>
          ))}
          {metadata.files.length > MAX_SHOWN_FILES && (
            <div className="text-xs text-muted-foreground">
              {t('+ {{count}} more files', { count: metadata.files.length - MAX_SHOWN_FILES })}
            </div>
          )}
        </div>
      )}
      {metadata.trackers.length > 0 && (
        <div className="space-y-1">
          <div className="text-sm font-medium">{t('Trackers')}</div>
          {metadata.trackers.slice(0, MAX_SHOWN_TRACKERS).map((tracker) => (
            <div key={tracker} className="font-mono text-xs text-muted-foreground truncate">
              {tracker}
            </div>
          ))}
          {metadata.trackers.length > MAX_SHOWN_TRACKERS && (
            <div className="text-xs text-muted-foreground">
              {t('+ {{count}} more', { count: metadata.trackers.length - MAX_SHOWN_TRACKERS })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
